import { useCallback } from "react";

import { type M365MailboxStatus } from "~/gql/graphqlBase.generated";
import {
	useGetM365MailboxStatusQuery,
	useLazyGetM365MailboxAuthorizeUrlQuery
} from "~/gql/queries/m365Mailbox.generated";
import logger from "~/helpers/logger";
import m365Mailbox from "~/helpers/m365Mailbox";
import snackMessages from "~/helpers/snackMessages";
import useM365MailboxMutations from "~/hooks/mutations/useM365MailboxMutations";
import { useSnacks } from "~/providers/SnackProvider";

const CONNECT_ACTION = "starting Microsoft sign-in";

export interface UseOutlookMailboxConnectReturn {
	connect: () => Promise<void>;
	connecting: boolean;
	disconnect: () => Promise<void>;
	isDisconnecting: boolean;
	isError: boolean;
	isLoading: boolean;
	status: M365MailboxStatus | undefined;
}

const useOutlookMailboxConnect = (): UseOutlookMailboxConnectReturn => {
	const [openSnack] = useSnacks();
	const { data, isError, isLoading, refetch } = useGetM365MailboxStatusQuery();
	const [getAuthorizeUrl, { isFetching: connecting }] = useLazyGetM365MailboxAuthorizeUrlQuery();
	const { disconnectMailbox, isDisconnecting } = useM365MailboxMutations();

	const status = data?.m365MailboxStatus;

	const connect = useCallback(async () => {
		m365Mailbox.rememberReturnPath();
		try {
			const result = await getAuthorizeUrl({ redirectUri: m365Mailbox.redirectUri() }).unwrap();
			window.location.assign(result.m365MailboxAuthorizeUrl);
		} catch (error) {
			logger.error(snackMessages.actionError(CONNECT_ACTION), error);
			openSnack(snackMessages.actionError(CONNECT_ACTION), "error");
		}
	}, [getAuthorizeUrl, openSnack]);

	const disconnect = useCallback(async () => {
		const disconnected = await disconnectMailbox();
		if (disconnected) refetch();
	}, [disconnectMailbox, refetch]);

	return { connect, connecting, disconnect, isDisconnecting, isError, isLoading, status };
};

export default useOutlookMailboxConnect;
